import React, { useState, useMemo, useEffect } from "react";
import {
    Box,
    Stack,
    Card,
    CardContent,
    CardHeader,
    Typography,
    Switch,
    Chip,
} from "@mui/material";
import { Button, TextField, MenuItem, Select, InputLabel, FormControl, CircularProgress } from "@mui/material";
import {
    Notifications as Bell,
    Search,
    FilterList as Filter,
    Add as Plus,
    Visibility as Eye,
    Group as Users,
    ReportProblem as AlertTriangle,
    Timeline as Activity,
    CheckCircle,
    VisibilityOff,
    AccessTime as Clock,
    ReadMore
} from "@mui/icons-material";
import ManageSearchIcon from '@mui/icons-material/ManageSearch';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';

import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
// import Link from "next/link";
import { format } from "date-fns";
import axios from "axios";
import { ApiLink } from "../../APILink/ApiLink";
import authHeader from "../../Auth/authHeader";
import { errorAlert, handleSuccess } from "../../Components/Alerts";
import CancelModal from "../../Components/CancelModal";

// Zëvendësim për hook dhe utils që nuk i ke
const useAppLevelAuth = () => ({ isLoggedIn: true });
const useToast = () => ({
    toast: ({ title, description }) => alert(`${title}\n${description}`)
});

export default function ManageNotifications({ getData, onEdit, canManage = false }) {
    const { toast } = useToast();
    const headers = authHeader();
    const [notifications, setNotifications] = useState([]);
    const [searchTerm, setSearchTerm] = useState("");
    const [filterActive, setFilterActive] = useState("all");
    const [filterRead, setFilterRead] = useState("all");
    const [filterVisibleTo, setFilterVisibleTo] = useState("all");
    const [filterVersion, setFilterVersion] = useState("all");
    const [filterPriority, setFilterPriority] = useState("all");
    const [page, setPage] = useState(1);
    const pageSize = 6;

    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showTop10Button, setShowTop10Button] = useState(true);

    const [showModal, setShowModal] = useState(false);
    const [deleteID, setDeleteid] = useState("");
    const [expandedMessages, setExpandedMessages] = useState({});

    const toggleMessage = (notificationId) => {
        setExpandedMessages((prev) => ({
            ...prev,
            [notificationId]: !prev[notificationId],
        }));
    };

    const fetchAllUserNotification = async (type) => {
        if (type === "all") {
            setIsSubmitting(true);
            setShowTop10Button(false);
        } else {
            setIsSubmitting(false);
            setShowTop10Button(true);
        }
        try {
            const response = await axios.get(ApiLink.API_URL + 'Notification/allNotification', { headers, params: { type: type } });
            // setCompanyID(response.data[0].CompanyID);
            setNotifications(response.data);
            setIsSubmitting(false);

        } catch (error) {
            errorAlert("Gabim në kërkesë:", error);
            setIsSubmitting(false);

            if (error.response && error.response.data && error.response.data.errorMessage) {
                errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
            } else if (error.message) {
                errorAlert(`Gabim: ${error.message}`);
            } else {
                errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
            }
        }
    };

    const deleteNotification = async (id) => {
        if (!canManage) return;
        try {
            const response = await axios.delete(`${ApiLink.API_URL}Notification/${id}`, { headers })
            handleSuccess("notifikimi u fshi me sukses")
            fetchAllUserNotification("top");
            setDeleteid("");
            setShowModal(false);
        }
        catch (e) {
            errorAlert("Nuk eshte fshire notifikimi")
        }
    }

    // const toggleActive = (id) => {
    //     setNotifications((prev) =>
    //         prev.map((notif) =>
    //             notif.id === id ? { ...notif, isActive: !notif.isActive } : notif
    //         )
    //     );

    //     const notification = notifications.find((n) => n.id === id);
    //     toast({
    //         title: "Status Updated",
    //         description: `Notification "${notification?.title}" is now ${notification?.isActive ? "inactive" : "active"
    //             }`,
    //     });
    // };

    const markAsRead = (id) => {
        setNotifications((prev) =>
            prev.map((notif) =>
                notif.id === id ? { ...notif, isRead: true } : notif
            )
        );
    };

    const filteredNotifications = useMemo(() => {
        const filtered = notifications.filter((notification) => {
            const matchesSearch =
                notification.Title.toLowerCase().includes(searchTerm.toLowerCase()) ||
                notification.Message.toLowerCase().includes(searchTerm.toLowerCase());

            const matchesActive =
                !canManage || filterActive === "all" ||
                (filterActive === "active" && notification.IsActive) ||
                (filterActive === "inactive" && !notification.IsActive);

            const matchesVisibleTo =
                !canManage || filterVisibleTo === "all" || notification.VisibleTo === filterVisibleTo;

            const matchesVersion =
                filterVersion === "all" || notification.Version === filterVersion;

            return (
                matchesSearch &&
                matchesActive &&
                (canManage || filterRead === "all" || (filterRead === "read" ? notification.IsRead : !notification.IsRead)) &&
                matchesVisibleTo &&
                matchesVersion
            );
        });

        return filtered.sort((a, b) => {
            if (a.IsRead !== b.IsRead) return Number(a.IsRead) - Number(b.IsRead);
            return new Date(b.CreatedDate) - new Date(a.CreatedDate);
        });
    }, [
        notifications,
        searchTerm,
        filterActive,
        filterRead,
        filterVisibleTo,
        filterVersion,
        canManage
    ]);

    const totalPages = Math.max(1, Math.ceil(filteredNotifications.length / pageSize));
    const paginatedNotifications = filteredNotifications.slice((page - 1) * pageSize, page * pageSize);

    useEffect(() => {
        setPage(1);
    }, [searchTerm, filterActive, filterRead, filterVisibleTo, filterVersion, canManage]);

    useEffect(() => {
        if (page > totalPages) {
            setPage(totalPages);
        }
    }, [page, totalPages]);

    // const getPriorityColor = (priority) => {
    //     switch (priority) {
    //         case "urgent":
    //             return "#f8d7da"; // red
    //         case "high":
    //             return "#fff3cd"; // orange
    //         case "medium":
    //             return "#d1ecf1"; // blue
    //         case "low":
    //             return "#e2e3e5"; // gray
    //         default:
    //             return "#e2e3e5";
    //     }
    // };

    const getVisibilityIcon = (visibleTo) => {
        switch (visibleTo) {
            case "All":
                return <Users fontSize="small" />;
            case "Admins":
                return <AlertTriangle fontSize="small" />;
            case "Staff":
                return <Activity fontSize="small" />;
            case "Custom":
                return <Users fontSize="small" />;
            default:
                return <Users fontSize="small" />;
        }
    };

    // const fetchAllUserNotification = async () => {
    //     try {
    //         const response = await axios.get(ApiLink.API_URL + 'Notification/allNotification', { headers });
    //         // setCompanyID(response.data[0].CompanyID);
    //         setNotifications(response.data);
    //         console.log(response.data);
    //     } catch (error) {
    //         errorAlert("Gabim në kërkesë:", error);

    //         if (error.response && error.response.data && error.response.data.errorMessage) {
    //             errorAlert(`Gabim në marrjen e të dhënave: ${error.response.data.errorMessage}`);
    //         } else if (error.message) {
    //             errorAlert(`Gabim: ${error.message}`);
    //         } else {
    //             errorAlert("Gabim i panjohur në kërkesë. Shih konsolën për detaje.");
    //         }
    //     }
    // };


    // if (!isLoggedIn) return null;


    const updateStatusNtfc = async (id) => {
        try {
            const response = await axios.put(
                ApiLink.API_URL + `Notification/statusNotification/${id}`,
                null, // ose {} — pasi nuk ka `data`
                { headers }
            );
            // handleSuccess("u editua");
            fetchAllUserNotification("top");

        } catch (e) {
            // errorAlert("Nuk është modifikuar", e.response);
        }
    };


    const uniqueVersions = [...new Set(notifications.map((n) => n.Version).filter(Boolean))];

    useEffect(() => {
        fetchAllUserNotification("top");
    }, [getData]);

    const unreadCount = notifications.filter((notification) => !notification.IsRead).length;

    return (
        <Box sx={{ maxWidth: 1280, mx: "auto", minWidth: 0 }}>
            <CancelModal
                open={canManage && showModal}
                onClose={() => setShowModal(false)}
                title="Konfirmim"
                description="Jeni të sigurt që dëshironi të fshini njoftimin?"
                confirmText="Po"
                cancelText="Jo"
                onConfirm={() => deleteNotification(deleteID)}
            />
            <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", gap: 2, mb: 3 }}>
                <Box sx={{ display: "grid", placeItems: "center", width: 52, height: 52, borderRadius: 3, bgcolor: "action.selected", color: "primary.main" }}>
                    <Bell />
                </Box>
                <Box sx={{ flex: 1, minWidth: 180 }}>
                    <Typography variant="h5" component="h2" fontWeight={700}>Njoftimet</Typography>
                    <Typography variant="body2" color="text.secondary">Përditësimet dhe informacionet e fundit nga Accountree.</Typography>
                </Box>
                <Chip label={`${unreadCount} të palexuara`} color={unreadCount ? "primary" : "default"} variant="outlined" />
                <Button variant="outlined" onClick={() => fetchAllUserNotification(showTop10Button ? "all" : "top")}
                    disabled={isSubmitting} startIcon={isSubmitting ? <CircularProgress size={18} /> : <ManageSearchIcon />}>
                    {isSubmitting ? "Duke u ngarkuar…" : showTop10Button ? "Shfaq të gjitha" : "Rifresko"}
                </Button>
            </Box>

            <Box sx={{ p: { xs: 2, md: 2.5 }, mb: 3, border: 1, borderColor: "divider", borderRadius: 3, bgcolor: "background.paper" }}>
                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", sm: "repeat(2, minmax(0, 1fr))", lg: canManage ? "2fr 1fr 1fr 1fr" : "2fr 1fr 1fr" }, gap: 2 }}>
                    <TextField fullWidth size="small" label="Kërko njoftime" value={searchTerm}
                        onChange={(e) => setSearchTerm(e.target.value)} placeholder="Titulli ose përmbajtja…"
                        InputProps={{ startAdornment: <Search sx={{ mr: 1, color: "text.secondary" }} /> }} />
                    <FormControl size="small" fullWidth>
                        <InputLabel id="notification-status-label">Statusi</InputLabel>
                        <Select labelId="notification-status-label" label="Statusi" value={canManage ? filterActive : filterRead} onChange={(e) => canManage ? setFilterActive(e.target.value) : setFilterRead(e.target.value)}>
                            <MenuItem value="all">Të gjitha</MenuItem>
                            <MenuItem value={canManage ? "active" : "unread"}>{canManage ? "Aktive" : "Të palexuara"}</MenuItem>
                            <MenuItem value={canManage ? "inactive" : "read"}>{canManage ? "Joaktive" : "Të lexuara"}</MenuItem>
                        </Select>
                    </FormControl>
                    {canManage && (
                        <FormControl size="small" fullWidth>
                            <InputLabel id="notification-audience-label">Audienca</InputLabel>
                            <Select labelId="notification-audience-label" label="Audienca" value={filterVisibleTo} onChange={(e) => setFilterVisibleTo(e.target.value)}>
                                <MenuItem value="all">Të gjitha audiencat</MenuItem>
                                <MenuItem value="All">Të gjithë përdoruesit</MenuItem>
                                <MenuItem value="Admins">Administratorët</MenuItem>
                                <MenuItem value="Staff">Stafi</MenuItem>
                                <MenuItem value="Custom">Kompani e caktuar</MenuItem>
                            </Select>
                        </FormControl>
                    )}
                    <FormControl size="small" fullWidth>
                        <InputLabel id="notification-version-label">Versioni</InputLabel>
                        <Select labelId="notification-version-label" label="Versioni" value={filterVersion} onChange={(e) => setFilterVersion(e.target.value)}>
                            <MenuItem value="all">Të gjitha versionet</MenuItem>
                            {uniqueVersions.map((version) => <MenuItem key={version} value={version}>{version}</MenuItem>)}
                        </Select>
                    </FormControl>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 2 }}>
                    Shfaqen {Math.min(filteredNotifications.length, page * pageSize)} nga {filteredNotifications.length} njoftime
                </Typography>
            </Box>

            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "minmax(0, 1fr)", md: "repeat(2, minmax(0, 1fr))" }, gap: 2.5, alignItems: "start" }}>
                {filteredNotifications.length === 0 ? (
                    <Box sx={{ gridColumn: "1 / -1", textAlign: "center", py: 7, px: 2, border: "1px dashed", borderColor: "divider", borderRadius: 3 }}>
                        <Bell sx={{ fontSize: 44, color: "text.disabled", mb: 1 }} />
                        <Typography variant="h6">Nuk ka njoftime për t’u shfaqur</Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                            {searchTerm || (canManage ? filterActive !== "all" || filterVisibleTo !== "all" : filterRead !== "all") || filterVersion !== "all"
                                ? "Provoni ta ndryshoni kërkimin ose filtrat."
                                : canManage ? "Krijoni njoftimin tuaj të parë." : "Njoftimet e reja do të shfaqen këtu."}
                        </Typography>
                    </Box>
                ) : (() => {
                    const unreadItems = paginatedNotifications.filter((notification) => !notification.IsRead);
                    const readItems = paginatedNotifications.filter((notification) => notification.IsRead);

                    const renderRow = (notification) => {
                        const isUnread = !notification.IsRead;
                        const expanded = Boolean(expandedMessages[notification.NotificationStatusID]);
                        const contentId = `notification-content-${notification.NotificationStatusID}`;
                        return (
                            <Box component="article" key={notification.NotificationStatusID}
                                sx={{ borderLeft: "3px solid", borderLeftColor: isUnread ? "primary.main" : "transparent", p: { xs: 2, sm: 2.5 }, "& + &": { borderTop: 1, borderTopColor: "divider" } }}>
                                <Box sx={{ display: "grid", gridTemplateColumns: { xs: "32px minmax(0, 1fr)", sm: "40px minmax(0, 1fr) 150px" }, columnGap: { xs: 1.5, sm: 2 }, rowGap: 1.5, alignItems: "start" }}>
                                    <Box sx={{ display: "grid", placeItems: "center", width: { xs: 32, sm: 40 }, height: { xs: 32, sm: 40 }, borderRadius: 2, bgcolor: isUnread ? "action.selected" : "action.hover", color: isUnread ? "primary.main" : "text.secondary" }}>
                                        {isUnread ? <Bell sx={{ fontSize: 20 }} /> : <CheckCircle sx={{ fontSize: 20 }} />}
                                    </Box>
                                    <Box sx={{ minWidth: 0 }}>
                                        <Typography component="h3" variant="subtitle1" fontWeight={isUnread ? 700 : 600} sx={{ lineHeight: 1.5, overflowWrap: "anywhere" }}>
                                            {notification.Title}
                                        </Typography>
                                        {!expanded && <Typography variant="body2" color="text.secondary" noWrap sx={{ mt: 0.5 }}>
                                            {notification.Message}
                                        </Typography>}
                                        <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap", columnGap: 1.5, rowGap: 0.5, mt: 1 }}>
                                            {isUnread ? <Chip label="E re" size="small" color="primary" variant="outlined" sx={{ height: 22, fontSize: 11 }} /> : <Typography variant="caption" color="text.secondary">E lexuar</Typography>}
                                            {notification.Version && <Typography variant="caption" color="text.secondary">v{notification.Version}</Typography>}
                                            {canManage && <Typography variant="caption" color="text.secondary">
                                                {({ All: "Të gjithë", Admins: "Administratorët", Staff: "Stafi", Custom: "Kompani e caktuar" })[notification.VisibleTo] || notification.VisibleTo}
                                            </Typography>}
                                            <Button size="small" aria-expanded={expanded} aria-controls={contentId}
                                                onClick={() => toggleMessage(notification.NotificationStatusID)}
                                                sx={{ p: 0, minWidth: 0, textTransform: "none", fontWeight: 600 }}>
                                                {expanded ? "Mbyll përmbajtjen" : "Lexo njoftimin"}
                                            </Button>
                                            {isUnread && <Button size="small" startIcon={<CheckCircle sx={{ fontSize: 16 }} />}
                                                onClick={() => updateStatusNtfc(notification.NotificationStatusID)}
                                                sx={{ textTransform: "none", fontWeight: 600 }}>
                                                Shëno si e lexuar
                                            </Button>}
                                        </Box>
                                    </Box>
                                    <Box sx={{ gridColumn: { xs: 2, sm: 3 }, display: "flex", flexDirection: { xs: "row", sm: "column" }, alignItems: { xs: "center", sm: "flex-end" }, justifyContent: "space-between", flexWrap: "wrap", gap: 0.5 }}>
                                        <Typography component="time" dateTime={notification.CreatedDate} variant="caption" color="text.secondary" sx={{ whiteSpace: "nowrap" }}>
                                            {format(new Date(notification.CreatedDate), "dd.MM.yyyy · HH:mm")}
                                        </Typography>
                                        {canManage && <Box sx={{ display: "flex", gap: 0.5 }}>
                                            <Tooltip title="Modifiko"><IconButton aria-label="Modifiko" size="small" onClick={() => onEdit(notification)}><EditIcon fontSize="small" /></IconButton></Tooltip>
                                            <Tooltip title="Fshi"><IconButton aria-label="Fshi" size="small" color="error" onClick={() => { setDeleteid(notification.NotificationID); setShowModal(true); }}><DeleteIcon fontSize="small" /></IconButton></Tooltip>
                                        </Box>}
                                    </Box>
                                    {expanded && <Box id={contentId} sx={{ gridColumn: "2 / -1", pt: 1.5, borderTop: 1, borderColor: "divider" }}>
                                        <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", overflowWrap: "anywhere", lineHeight: 1.85 }}>
                                            {notification.Message}
                                        </Typography>
                                    </Box>}
                                </Box>
                            </Box>
                        );
                    };

                    return (
                        <>
                            {unreadItems.length > 0 && <>
                                <Box sx={{ gridColumn: "1 / -1", display: "flex", alignItems: "center", gap: 1.5 }}>
                                    <Typography variant="subtitle2" color="text.primary">Njoftimet e palexuara</Typography>
                                    <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
                                </Box>
                                <Box sx={{ gridColumn: "1 / -1", border: 1, borderColor: "divider", borderRadius: 3, overflow: "hidden", bgcolor: "background.paper" }}>
                                    {unreadItems.map(renderRow)}
                                </Box>
                            </>}
                            {readItems.length > 0 && (
                                <Box sx={{ gridColumn: "1 / -1", mt: unreadItems.length ? 2 : 0, display: "flex", alignItems: "center", gap: 1.5 }}>
                                    <Typography variant="subtitle2" color="text.secondary" sx={{ flexShrink: 0 }}>
                                        Njoftimet e lexuara
                                    </Typography>
                                    <Box sx={{ flex: 1, height: "1px", bgcolor: "divider" }} />
                                </Box>
                            )}
                            {readItems.length > 0 && (
                                <Box sx={{ gridColumn: "1 / -1", border: 1, borderColor: "divider", borderRadius: 3, overflow: "hidden", bgcolor: "background.paper" }}>
                                    {readItems.map(renderRow)}
                                </Box>
                            )}
                        </>
                    );
                })()}
            </Box>

            {filteredNotifications.length > pageSize && (
                <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", gap: 1, mt: 3, flexWrap: "wrap" }}>
                    <Button
                        variant="outlined"
                        size="small"
                        disabled={page === 1}
                        onClick={() => setPage((prev) => Math.max(prev - 1, 1))}
                    >
                        Mbrapa
                    </Button>

                    <Typography variant="body2" color="text.secondary" sx={{ px: 1 }}>
                        Faqja {page} / {totalPages}
                    </Typography>

                    <Button
                        variant="outlined"
                        size="small"
                        disabled={page >= totalPages}
                        onClick={() => setPage((prev) => Math.min(prev + 1, totalPages))}
                    >
                        Para
                    </Button>
                </Box>
            )}
        </Box>
    );
}
